import type { Ref } from 'vue';
import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useStorage } from '@n8n/composables/useStorage';
import { useUsersStore } from '@n8n/stores/users.store';
import { useRootStore } from '@n8n/stores/useRootStore';
import { useSettingsStore } from '@n8n/stores/settings.store';
import type { FeatureFlags, IDataObject } from 'n8n-workflow';
import { EXPERIMENTS_TO_TRACK, LOCAL_STORAGE_EXPERIMENT_OVERRIDES } from '@/app/constants';
import { TELEMETRY_EVENT } from '@n8n/telemetry';
import { useDebounce } from '@n8n/composables/useDebounce';
import { useTelemetry } from '@n8n/composables/useTelemetry';

const POSTHOG_GROUP_TYPE_INSTANCE = 'company';

export type PosthogStore = ReturnType<typeof usePostHog>;

export const usePostHog = defineStore('posthog', () => {
	const usersStore = useUsersStore();
	const settingsStore = useSettingsStore();
	const telemetry = useTelemetry();
	const rootStore = useRootStore();
	const { debounce } = useDebounce();

	const featureFlags: Ref<FeatureFlags | null> = ref(null);
	const trackedDemoExp: Ref<FeatureFlags> = ref({});
	const trackedExposures: Ref<FeatureFlags> = ref({});
	const pendingFeatureFlagsEvaluation = ref(false);

	const overrides: Ref<Record<string, string | boolean>> = ref({});
	let featureFlagsWaitPromise: Promise<FeatureFlags | null> | null = null;
	let resolveFeatureFlagsWait: ((flags: FeatureFlags | null) => void) | null = null;

	const clearFeatureFlagsWait = () => {
		featureFlagsWaitPromise = null;
		resolveFeatureFlagsWait = null;
	};

	const resolveFeatureFlagsWaiters = (flags: FeatureFlags | null) => {
		pendingFeatureFlagsEvaluation.value = false;

		if (resolveFeatureFlagsWait) {
			resolveFeatureFlagsWait(flags);
		}

		clearFeatureFlagsWait();
	};

	const reset = () => {
		window.posthog?.reset?.();
		featureFlags.value = null;
		trackedDemoExp.value = {};
		trackedExposures.value = {};
		pendingFeatureFlagsEvaluation.value = false;
		clearFeatureFlagsWait();
	};

	const getVariant = (experiment: keyof FeatureFlags): FeatureFlags[keyof FeatureFlags] => {
		return overrides.value[experiment] ?? featureFlags.value?.[experiment];
	};

	const isVariantEnabled = (experiment: string, variant: string) => {
		return getVariant(experiment) === variant;
	};

	/**
	 * Checks if the given feature flag is enabled. Should only be used for boolean flags
	 */
	const isFeatureEnabled = (experiment: keyof FeatureFlags) => {
		return getVariant(experiment) === true;
	};

	const hasPendingFeatureFlags = () => pendingFeatureFlagsEvaluation.value;

	const waitForFeatureFlags = async () => {
		if (!pendingFeatureFlagsEvaluation.value) {
			return featureFlags.value;
		}

		if (!featureFlagsWaitPromise) {
			featureFlagsWaitPromise = new Promise((resolve) => {
				resolveFeatureFlagsWait = resolve;
			});
		}

		return await featureFlagsWaitPromise;
	};

	if (!window.featureFlags) {
		// for testing
		const cachedOverrides = useStorage(LOCAL_STORAGE_EXPERIMENT_OVERRIDES).value;
		if (cachedOverrides) {
			try {
				console.log('Overriding feature flags', cachedOverrides);
				const parsedOverrides = JSON.parse(cachedOverrides);
				if (typeof parsedOverrides === 'object') {
					overrides.value = JSON.parse(cachedOverrides);
				}
			} catch (e) {
				console.log('Could not override experiment', e);
			}
		}

		window.featureFlags = {
			// since features are evaluated serverside, regular posthog mechanism to override clientside does not work
			override: (name: string, value: string | boolean) => {
				overrides.value[name] = value;
				try {
					useStorage(LOCAL_STORAGE_EXPERIMENT_OVERRIDES).value = JSON.stringify(overrides.value);
				} catch (e) {}
			},

			getVariant,
			getAll: () => featureFlags.value ?? {},
		};
	}

	const groupIdentify = (groupKey: string, instanceId: string) => {
		window.posthog?.group?.(groupKey, instanceId);
	};

	const identify = () => {
		const instanceId = rootStore.instanceId;
		const user = usersStore.currentUser;
		if (!user) return;

		const versionCli = rootStore.versionCli;
		const traits: Record<string, string | number> = {
			instance_id: instanceId,
			version_cli: versionCli,
		};

		if (typeof user.createdAt === 'string') {
			traits.created_at_timestamp = new Date(user.createdAt).getTime();
		}

		window.posthog?.identify?.(`${instanceId}#${user.id}`, traits);
	};

	const trackExperiment = (featFlags: FeatureFlags, name: string) => {
		const variant = featFlags[name];
		if (!variant || trackedDemoExp.value[name] === variant) {
			return;
		}

		telemetry.track(TELEMETRY_EVENT.PLATFORM.USER_IS_PART_OF_EXPERIMENT, {
			name,
			variant,
		});

		trackedDemoExp.value[name] = variant;
	};

	const trackExperiments = (featFlags: FeatureFlags) => {
		EXPERIMENTS_TO_TRACK.forEach((name) => trackExperiment(featFlags, name));
	};
	const trackExperimentsDebounced = debounce(trackExperiments, {
		debounceTime: 2000,
	});

	const init = (evaluatedFeatureFlags?: FeatureFlags) => {
		if (!window.posthog) {
			return;
		}

		const config = settingsStore.settings.posthog;
		if (!config.enabled) {
			return;
		}

		const userId = usersStore.currentUserId;
		if (!userId) {
			return;
		}

		const instanceId = rootStore.instanceId;
		const distinctId = `${instanceId}#${userId}`;

		const options: Parameters<typeof window.posthog.init>[1] = {
			api_host: settingsStore.settings.posthog.proxy,
			autocapture: config.autocapture,
			disable_session_recording: config.disableSessionRecording,
			debug: false, // Enables console logs for debugging reasons
			session_recording: {
				maskAllInputs: false,
			},
			...(!config.disableSessionRecording && {
				tracing_headers: [new URL(rootStore.restUrl, window.location.origin).hostname],
			}),
		};

		if (evaluatedFeatureFlags && Object.keys(evaluatedFeatureFlags).length) {
			options.bootstrap = {
				distinctID: distinctId,
				featureFlags: evaluatedFeatureFlags,
			};
			// Flags are server-evaluated and bootstrapped; without this, identify()/group()
			// in the loaded callback each trigger a billed /flags refetch of values the
			// client already has.
			options.advanced_disable_feature_flags = true;
		}

		window.posthog?.init(config.apiKey, {
			...options,
			loaded: () => {
				identify();
				groupIdentify(POSTHOG_GROUP_TYPE_INSTANCE, instanceId);
			},
		});

		if (evaluatedFeatureFlags && Object.keys(evaluatedFeatureFlags).length) {
			featureFlags.value = evaluatedFeatureFlags;
			resolveFeatureFlagsWaiters(featureFlags.value);

			// does not need to be debounced really, but tracking does not fire without delay on page load
			trackExperimentsDebounced(featureFlags.value);
		} else {
			// depend on client side evaluation if serverside evaluation fails
			pendingFeatureFlagsEvaluation.value = true;
			window.posthog?.onFeatureFlags?.((_, map: FeatureFlags) => {
				featureFlags.value = map;
				resolveFeatureFlagsWaiters(featureFlags.value);

				// must be debounced because it is called multiple times by posthog
				trackExperimentsDebounced(featureFlags.value);
			});
		}
	};

	const setMetadata = (metadata: IDataObject, target: 'user' | 'events') => {
		if (typeof window.posthog?.people?.set !== 'function') return;
		if (typeof window.posthog?.register !== 'function') return;

		if (target === 'user') {
			window.posthog?.people?.set(metadata);
		} else if (target === 'events') {
			window.posthog?.register(metadata);
		}
	};

	const capture = (event: string, properties: IDataObject = {}) => {
		if (typeof window.posthog?.capture === 'function') {
			window.posthog.capture(event, properties);
		}
	};

	/**
	 * Fires PostHog's native exposure event for an experiment so results can use
	 * built-in exposure analysis. Uses getVariant() so the recorded variant is the
	 * one the user actually experienced, including local overrides. Deduped per
	 * flag+variant; cleared on reset().
	 */
	const trackExposure = (experiment: string) => {
		const variant = getVariant(experiment);
		if (variant === undefined || variant === false) return;
		if (trackedExposures.value[experiment] === variant) return;
		capture('$feature_flag_called', {
			$feature_flag: experiment,
			$feature_flag_response: variant,
		});
		trackedExposures.value[experiment] = variant;
	};

	return {
		init,
		isFeatureEnabled,
		isVariantEnabled,
		getVariant,
		hasPendingFeatureFlags,
		waitForFeatureFlags,
		reset,
		identify,
		groupIdentify,
		setMetadata,
		capture,
		trackExposure,
		overrides,
	};
});
